import React, { useState, useEffect, useRef } from 'react';
import { User, Settings, Shield, LogOut, ChevronRight, Bell } from 'lucide-react';
import { NotificationBell } from './NotificationBell';
import { useAuth } from '../../contexts/AuthContext';
import { useSwitchUser } from '../../contexts/SwitchUserContext';
import { useAbilityContext } from '../../contexts/AbilityContext';
import ThemeToggle from './ThemeToggle';
import AppLauncher from './AppLauncher';
import { apps } from '../../data/mockData';

export interface MinimalAppHeaderProps {
  appTitle: string;
  currentView?: string;
  onAppSelect?: (app: { name: string }) => void;
  onBackToHome: () => void;
  onNavigate: (view: string) => void;
  userRole?: string;
}

const MinimalAppHeader: React.FC<MinimalAppHeaderProps> = ({
  appTitle,
  currentView = '',
  onAppSelect,
  onBackToHome,
  onNavigate,
  userRole = '',
}) => {
  const [showAppLauncher, setShowAppLauncher] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const appLauncherRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Prefer the resolved Gobodo role (what RBAC actually grants) over the
  // userRole prop, which comes from App.tsx getUserRole() — that function
  // falls back to getMVPDefaultRole() = 'system_admin' for any user whose
  // Keycloak realm roles don't match a hardcoded list. Same source as the
  // RBAC Debug panel.
  const { role: resolvedRole } = useAbilityContext();
  const displayRole = resolvedRole?.name || userRole;
  const { user, logout } = useAuth();
  const { isSwitched } = useSwitchUser();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (appLauncherRef.current && !appLauncherRef.current.contains(event.target as Node)) {
        setShowAppLauncher(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header
      className={`bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700 fixed left-0 right-0 z-50 ${isSwitched ? 'top-11' : 'top-0'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-4">
            <div className="relative" ref={appLauncherRef}>
              <button
                onClick={() => (onAppSelect ? setShowAppLauncher(!showAppLauncher) : onBackToHome())}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
              >
                <img src="/regal-logo.svg" alt="Regal" className="w-12 h-12" />
                {onAppSelect && <ChevronRight className="h-4 w-4" />}
              </button>
              {showAppLauncher && onAppSelect && (
                <AppLauncher
                  apps={apps}
                  currentApp={currentView}
                  onAppSelect={onAppSelect}
                  onClose={() => setShowAppLauncher(false)}
                />
              )}
            </div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 border-l border-gray-200 dark:border-gray-600 pl-4">
              {appTitle}
            </span>
          </div>

          <div className="flex items-center space-x-4">
            {/* ThemeToggle hidden until dark mode is fully supported */}
            <NotificationBell />
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <User className="h-5 w-5" />
              </button>
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-600">
                  <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {user?.email || 'User'}
                    </p>
                    {displayRole && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Role: {displayRole}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      onNavigate('profile');
                      setShowUserMenu(false);
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <User className="h-4 w-4 mr-2" />
                    Your Profile
                  </button>
                  <button
                    onClick={() => {
                      onNavigate('settings');
                      setShowUserMenu(false);
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </button>
                  <button
                    onClick={() => {
                      onNavigate('support-tickets');
                      setShowUserMenu(false);
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Support Tickets
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
                  <button
                    onClick={() => {
                      onNavigate('system-admin');
                      setShowUserMenu(false);
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    System Admin
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
                  <button
                    onClick={async () => {
                      setShowUserMenu(false);
                      await logout();
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default MinimalAppHeader;
